import React from "react";
import { useParams, Link } from "react-router-dom";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AreaDetail() {
  const { slug } = useParams();

  const { data: area, isLoading } = useQuery({
    queryKey: ["area", slug],
    queryFn: async () => {
      const results = await base44.entities.AreaPages.filter({ slug });
      return results[0] || null;
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["area-projects", area?.featured_project_ids],
    queryFn: async () => {
      if (!area?.featured_project_ids?.length) return [];
      const all = await base44.entities.PortfolioProjects.list("-completion_date", 20);
      return all.filter((p) => area.featured_project_ids.includes(p.id)).slice(0, 6);
    },
    enabled: !!area,
    initialData: [],
  });

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-muted border-t-accent rounded-full animate-spin" />
    </div>
  );

  if (!area) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="font-body text-muted-foreground mb-4">Area page not found.</p>
        <Link to="/areas"><Button>View All Areas</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Top-Notch Contractors",
        "description": area.meta_description,
        "areaServed": { "@type": "City", "name": area.city, "addressRegion": "MD" },
      })}} />

      {/* Hero */}
      <section className="bg-primary py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">
            <Link to="/areas" className="hover:text-accent/80 transition-colors">Service Areas</Link> / {area.city}
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-2xl leading-tight">
            {area.h1}
          </h1>
        </div>
      </section>

      {/* Content + sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="font-body text-foreground text-lg leading-relaxed mb-8">{area.intro}</p>
            {area.local_content && (
              <div className="bg-secondary/60 rounded-xl p-7 border border-border mb-10">
                <h2 className="font-heading text-xl font-bold text-foreground mb-3">About the {area.city} Area</h2>
                <p className="font-body text-muted-foreground leading-relaxed">{area.local_content}</p>
              </div>
            )}

            {projects.length > 0 && (
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                  Recent Projects Near {area.city}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {projects.map((p) => {
                    const img = p.after_image || (p.gallery_images && p.gallery_images[0]);
                    return (
                      <Link key={p.id} to="/portfolio" className="group block bg-card rounded-xl overflow-hidden border border-border hover:shadow-md transition-all">
                        {img && <img loading="lazy" decoding="async" src={img} alt={p.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />}
                        <div className="p-4">
                          <h3 className="font-heading font-bold text-foreground group-hover:text-accent transition-colors">{p.title}</h3>
                          {p.location && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground font-body mt-1">
                              <MapPin className="w-3 h-3" /> {p.location}
                            </p>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div>
            <div className="sticky top-32 space-y-5">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                  Serving {area.city}
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-5">
                  Licensed, insured, and active in {area.city} since 2010. Free in-home consultations available.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-accent text-white hover:bg-accent/90 font-body font-semibold mb-3">
                    Free Consultation
                  </Button>
                </Link>
                <a href="tel:4105989933">
                  <Button variant="outline" className="w-full border-primary/30 text-primary hover:bg-primary hover:text-white font-body transition-colors">
                    <Phone className="w-4 h-4 mr-2" /> (410) 598-9933
                  </Button>
                </a>
              </div>
              <div className="bg-secondary/60 border border-border rounded-xl p-5 font-body text-sm space-y-1.5 text-muted-foreground">
                <p className="font-semibold text-foreground">Licensed &amp; Insured</p>
                <p>MHIC# 05-147079</p>
                <p>PA HIC# PA177445</p>
                <p>500+ Projects Completed</p>
                <p>Since 2010</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-5">
                <p className="font-body text-sm font-semibold text-foreground mb-3">Our Services</p>
                {["Kitchen Remodeling", "Bathroom Remodeling", "Basement Finishing", "Whole-House Renovation", "Exterior Remodeling"].map((s) => (
                  <div key={s} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0">
                    <ArrowRight className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                    <span className="font-body text-sm text-muted-foreground">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}